import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	getMyCompany,
	updateCompany,
	getActualites,
	addActuality,
	updateActuality,
	deleteActuality,
	getCertificates,
	addCertificate,
	updateCertificate,
	deleteCertificate,
} from '../../../api/redux/slices/Admin';
import { imageApi } from '../../../api/redux/slices/Public';
import { showToast } from '../../../components/modal';
import './index.scss';

/* ─── ImageUploader ─── */
const ImageUploader = memo(({ value, onChange, token, label = 'Image', required = false, id, variant = 'default' }) => {
	const dispatch = useDispatch();
	const [preview, setPreview] = useState(value?.secure_url || '');
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState('');
	const inputRef = useRef();
	const inputId = id || `img-upload-${label.replace(/\s+/g, '-').toLowerCase()}`;

	useEffect(() => {
		setPreview(value?.secure_url || '');
	}, [value?.secure_url]);

	const handleFile = useCallback(
		async (file) => {
			if (!file) return;
			const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
			if (!allowed.includes(file.type)) {
				setError('Type de fichier invalide (png, jpg uniquement)');
				showToast('Type de fichier invalide (png, jpg uniquement)', 'error');
				return;
			}
			setError('');
			setUploading(true);
			const formData = new FormData();
			formData.append('image', file);
			try {
				const result = await dispatch(imageApi({ token, formDta: formData })).unwrap();
				setPreview(result.secure_url);
				onChange(result);
				showToast('Image téléchargée avec succès', 'success');
			} catch {
				setError('Erreur lors du téléchargement');
				showToast("Erreur lors du téléchargement de l'image", 'error');
			} finally {
				setUploading(false);
			}
		},
		[dispatch, token, onChange],
	);

	const handleDrop = useCallback(
		(e) => {
			e.preventDefault();
			handleFile(e.dataTransfer.files[0]);
		},
		[handleFile],
	);

	return (
		<div className={`image-uploader ${variant === 'modal' ? 'modal-uploader' : ''}`}>
			<label htmlFor={inputId} className='uploader-label'>
				{label}
				{required && ' *'}
			</label>
			<div
				role='button'
				tabIndex={0}
				aria-label={`Télécharger ${label}`}
				className={`drop-zone ${uploading ? 'uploading' : ''} ${preview ? 'has-preview' : ''}`}
				onClick={() => !uploading && inputRef.current?.click()}
				onKeyDown={(e) => e.key === 'Enter' && !uploading && inputRef.current?.click()}
				onDrop={handleDrop}
				onDragOver={(e) => e.preventDefault()}
			>
				{uploading ? (
					<div className='upload-spinner'>
						<div className='spinner' aria-hidden='true' />
						<span>Téléchargement...</span>
					</div>
				) : preview ? (
					<div className='preview-wrap'>
						<img src={preview} alt={`Aperçu ${label}`} className='preview-img' />
						<div className='preview-overlay' aria-hidden='true'>
							<span>Changer l'image</span>
						</div>
					</div>
				) : (
					<div className='drop-placeholder'>
						<svg
							width='32'
							height='32'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='1.5'
							aria-hidden='true'
						>
							<path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
							<polyline points='17 8 12 3 7 8' />
							<line x1='12' y1='3' x2='12' y2='15' />
						</svg>
						<span>
							Glisser-déposer ou <u>parcourir</u>
						</span>
						<small>PNG, JPG — max 1MB</small>
					</div>
				)}
				<input
					ref={inputRef}
					id={inputId}
					type='file'
					accept='image/png,image/jpeg,image/jpg'
					style={{ display: 'none' }}
					onChange={(e) => handleFile(e.target.files[0])}
					aria-label={`Choisir un fichier pour ${label}`}
				/>
			</div>
			{error && (
				<div className='upload-error' role='alert'>
					{error}
				</div>
			)}
		</div>
	);
});

/* ─── Actuality Card ─── */
const ActualityCard = memo(({ actuality, onEdit, onDelete }) => (
	<article className='actuality-card'>
		{actuality.image?.secure_url && (
			<div className='card-image'>
				<img src={actuality.image.secure_url} alt={actuality.title} loading='lazy' />
			</div>
		)}
		<div className='card-body'>
			<h3>{actuality.title}</h3>
			{actuality.description && <p>{actuality.description}</p>}
			<time dateTime={actuality.date}>{new Date(actuality.date).toLocaleDateString('fr-FR')}</time>
		</div>
		<div className='card-actions'>
			<button
				className='action-btn edit'
				onClick={() => onEdit(actuality)}
				aria-label={`Modifier ${actuality.title}`}
				title='Modifier'
				type='button'
			>
				<svg
					width='15'
					height='15'
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='2'
					aria-hidden='true'
				>
					<path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' />
					<path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' />
				</svg>
			</button>
			<button
				className='action-btn delete'
				onClick={() => onDelete(actuality._id)}
				aria-label={`Supprimer ${actuality.title}`}
				title='Supprimer'
				type='button'
			>
				<svg
					width='15'
					height='15'
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='2'
					aria-hidden='true'
				>
					<polyline points='3 6 5 6 21 6' />
					<path d='M19 6l-1 14H6L5 6' />
					<path d='M10 11v6' />
					<path d='M14 11v6' />
					<path d='M9 6V4h6v2' />
				</svg>
			</button>
		</div>
	</article>
));

/* ─── Certificate Card ─── */
const CertificateCard = memo(({ certificate, onEdit, onDelete }) => (
	<article className='certificate-card'>
		{certificate.image?.secure_url && (
			<div className='card-image cert-img'>
				<img src={certificate.image.secure_url} alt={certificate.name} loading='lazy' />
			</div>
		)}
		<div className='card-body'>
			<h3>{certificate.name}</h3>
		</div>
		<div className='card-actions'>
			<button
				className='action-btn edit'
				onClick={() => onEdit(certificate)}
				aria-label={`Modifier ${certificate.name}`}
				title='Modifier'
				type='button'
			>
				<svg
					width='15'
					height='15'
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='2'
					aria-hidden='true'
				>
					<path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' />
					<path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' />
				</svg>
			</button>
			<button
				className='action-btn delete'
				onClick={() => onDelete(certificate._id)}
				aria-label={`Supprimer ${certificate.name}`}
				title='Supprimer'
				type='button'
			>
				<svg
					width='15'
					height='15'
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='2'
					aria-hidden='true'
				>
					<polyline points='3 6 5 6 21 6' />
					<path d='M19 6l-1 14H6L5 6' />
					<path d='M10 11v6' />
					<path d='M14 11v6' />
					<path d='M9 6V4h6v2' />
				</svg>
			</button>
		</div>
	</article>
));

/* ─── Modal ─── */
const Modal = memo(({ title, onClose, onSubmit, loading, children, submitLabel }) =>
	createPortal(
		<div
			className='modal-overlay'
			onClick={onClose}
			role='dialog'
			aria-modal='true'
			aria-labelledby='modal-title'
		>
			<div className='modal' onClick={(e) => e.stopPropagation()}>
				<div className='modal-header'>
					<h2 id='modal-title'>{title}</h2>
					<button className='modal-close' onClick={onClose} aria-label='Fermer' type='button'>
						×
					</button>
				</div>
				<form onSubmit={onSubmit} className='modal-body' noValidate>
					{children}
					<div className='modal-footer'>
						<button type='button' className='btn btn-ghost' onClick={onClose}>
							Annuler
						</button>
						<button type='submit' className='btn btn-primary' disabled={loading}>
							{loading ? (
								<>
									<span className='btn-spinner' aria-hidden='true' /> Envoi...
								</>
							) : (
								submitLabel
							)}
						</button>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	),
);

/* ─── Delete Confirm Modal ─── */
const DeleteModal = memo(({ onClose, onConfirm, loading }) =>
	createPortal(
		<div
			className='modal-overlay'
			onClick={onClose}
			role='alertdialog'
			aria-modal='true'
			aria-labelledby='delete-modal-title'
			aria-describedby='delete-modal-desc'
		>
			<div className='modal delete-modal' onClick={(e) => e.stopPropagation()}>
				<div className='modal-header'>
					<h2 id='delete-modal-title'>Confirmation de suppression</h2>
					<button className='modal-close' onClick={onClose} aria-label='Fermer' type='button'>
						×
					</button>
				</div>
				<div className='modal-body'>
					<p id='delete-modal-desc'>
						Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.
					</p>
				</div>
				<div className='modal-footer'>
					<button type='button' className='btn btn-ghost' onClick={onClose}>
						Annuler
					</button>
					<button type='button' className='btn btn-primary' onClick={onConfirm} disabled={loading}>
						{loading ? 'Suppression...' : 'Supprimer'}
					</button>
				</div>
			</div>
		</div>,
		document.body,
	),
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const CompanyProfile = () => {
	const dispatch = useDispatch();
	const { company, actualites, certificats } = useSelector((state) => state.admin);
	const [loading, setLoading] = useState(false);
	const [activeTab, setActiveTab] = useState('info');
	const token = localStorage.getItem('accesstoken');

	// Options pour les catégories d'économie
	const economyCategories = [
		{ value: 'economie_vert', label: '🌱 Économie verte' },
		{ value: 'economie_sociale', label: '🤝 Économie sociale' },
		{ value: 'economie_circulaire', label: '🔄 Économie circulaire' },
		{ value: 'economie_numerique', label: '💻 Économie numérique' },
		{ value: 'economie_bleue', label: '🌊 Économie bleue' },
	];

	// Options pour le type de société
	const companyTypes = [
		{ value: 'SARL', label: 'SARL' },
		{ value: 'SUARL', label: 'SUARL' },
		{ value: 'SA', label: 'SA' },
		{ value: 'SAS', label: 'SAS' },
		{ value: 'SNC', label: 'SNC' },
		{ value: 'SCS', label: 'SCS' },
		{ value: 'SCA', label: 'SCA' },
		{ value: 'EURL', label: 'EURL' },
		{ value: 'Entreprise_Individuelle', label: 'Entreprise Individuelle' },
		{ value: 'Autre', label: 'Autre' },
	];

	const [companyForm, setCompanyForm] = useState({
		name: '',
		address: '',
		phone: '',
		fax: '',
		email: '',
		tva_code: '',
		r_c: '',
		customs_code: '',
		siteweb: '',
		sponsorship_code: '',
		sponsorTotal: 0,
		date_creation_societe: '',
		image: { public_id: '', secure_url: '' },
		nombre_employes: 0,
		categories_economie: [],
		company_type: 'SARL',
	});

	const [actualityModal, setActualityModal] = useState({ open: false, editing: null });
	const [certificateModal, setCertificateModal] = useState({ open: false, editing: null });
	const [deleteModal, setDeleteModal] = useState({ open: false, type: null, id: null });

	const [actualityForm, setActualityForm] = useState({
		title: '',
		description: '',
		image: { public_id: '', secure_url: '' },
	});
	const [certificateForm, setCertificateForm] = useState({
		name: '',
		image: { public_id: '', secure_url: '' },
	});

	/* ── Fetch ── */
	const fetchAll = useCallback(async () => {
		setLoading(true);
		try {
			await Promise.all([
				dispatch(getMyCompany({ token })).unwrap(),
				dispatch(getActualites({ token })).unwrap(),
				dispatch(getCertificates({ token })).unwrap(),
			]);
		} catch {
			showToast("Quelque chose s'est mal passé !", 'error');
		} finally {
			setLoading(false);
		}
	}, [dispatch, token]);

	useEffect(() => {
		if (token) fetchAll();
	}, [fetchAll, token]);

	useEffect(() => {
		if (!company) return;
		let formattedDate = '';
		if (company.date_creation_societe) {
			const d = new Date(company.date_creation_societe);
			if (!isNaN(d.getTime())) formattedDate = d.toISOString().split('T')[0];
		}
		setCompanyForm({
			name: company.name || '',
			address: company.address || '',
			phone: company.phone || '',
			fax: company.fax || '',
			email: company.email || '',
			tva_code: company.tva_code || '',
			r_c: company.r_c || '',
			customs_code: company.customs_code || '',
			siteweb: company.siteweb || '',
			sponsorship_code: company.sponsorship_code || '',
			sponsorTotal: company.sponsorTotal || 0,
			date_creation_societe: formattedDate,
			image: company.image || { public_id: '', secure_url: '' },
			nombre_employes: company.nombre_employes || 0,
			categories_economie: company.categories_economie || [],
			company_type: company.company_type || 'SARL',
		});
	}, [company]);

	/* ── Company ── */
	const handleCompanyChange = useCallback((e) => {
		const { name, value } = e.target;
		setCompanyForm((prev) => ({ ...prev, [name]: value }));
	}, []);

	const handleCategoryChange = useCallback((categoryValue) => {
		setCompanyForm((prev) => {
			const currentCategories = [...prev.categories_economie];
			if (currentCategories.includes(categoryValue)) {
				return {
					...prev,
					categories_economie: currentCategories.filter(cat => cat !== categoryValue),
				};
			} else {
				return {
					...prev,
					categories_economie: [...currentCategories, categoryValue],
				};
			}
		});
	}, []);

	const handleCompanySubmit = useCallback(
		async (e) => {
			e.preventDefault();
			setLoading(true);
			try {
				const submitData = {
					name: companyForm.name,
					address: companyForm.address === '' ? null : companyForm.address || null,
					phone: companyForm.phone === '' ? null : companyForm.phone || null,
					fax: companyForm.fax === '' ? null : companyForm.fax || null,
					email: companyForm.email === '' ? null : companyForm.email || null,
					tva_code: companyForm.tva_code === '' ? null : companyForm.tva_code || null,
					r_c: companyForm.r_c === '' ? null : companyForm.r_c || null,
					customs_code: companyForm.customs_code === '' ? null : companyForm.customs_code || null,
					siteweb: companyForm.siteweb === '' ? null : companyForm.siteweb || null,
					sponsorship_code: companyForm.sponsorship_code || undefined,
					date_creation_societe: companyForm.date_creation_societe
						? new Date(companyForm.date_creation_societe)
						: null,
					sponsorTotal: companyForm.sponsorTotal,
					image: companyForm.image,
					nombre_employes: companyForm.nombre_employes || 0,
					categories_economie: companyForm.categories_economie,
					company_type: companyForm.company_type || 'SARL',
				};
				await dispatch(updateCompany({ token, data: submitData })).unwrap();
				await dispatch(getMyCompany({ token })).unwrap();
				showToast('Entreprise mise à jour avec succès', 'success');
			} catch (err) {
				showToast(err?.message || 'Erreur lors de la mise à jour', 'error');
			} finally {
				setLoading(false);
			}
		},
		[dispatch, token, companyForm],
	);

	/* ── Actualities ── */
	const openActualityModal = useCallback((actuality = null) => {
		setActualityModal({ open: true, editing: actuality });
		setActualityForm(
			actuality
				? {
						title: actuality.title || '',
						description: actuality.description || '',
						image: actuality.image || { public_id: '', secure_url: '' },
				  }
				: { title: '', description: '', image: { public_id: '', secure_url: '' } },
		);
	}, []);

	const closeActualityModal = useCallback(
		() => setActualityModal({ open: false, editing: null }),
		[],
	);

	const handleActualitySubmit = useCallback(
		async (e) => {
			e.preventDefault();
			setLoading(true);
			try {
				if (actualityModal.editing) {
					await dispatch(
						updateActuality({
							token,
							actualityId: actualityModal.editing._id,
							data: actualityForm,
						}),
					).unwrap();
					showToast('Actualité modifiée avec succès', 'success');
				} else {
					await dispatch(addActuality({ token, data: actualityForm })).unwrap();
					showToast('Actualité ajoutée avec succès', 'success');
				}
				await dispatch(getActualites({ token })).unwrap();
				closeActualityModal();
			} catch (err) {
				showToast(err?.message || "Quelque chose s'est mal passé !", 'error');
			} finally {
				setLoading(false);
			}
		},
		[dispatch, token, actualityForm, actualityModal, closeActualityModal],
	);

	/* ── Certificates ── */
	const openCertificateModal = useCallback((certificate = null) => {
		setCertificateModal({ open: true, editing: certificate });
		setCertificateForm(
			certificate
				? {
						name: certificate.name || '',
						image: certificate.image || { public_id: '', secure_url: '' },
				  }
				: { name: '', image: { public_id: '', secure_url: '' } },
		);
	}, []);

	const closeCertificateModal = useCallback(
		() => setCertificateModal({ open: false, editing: null }),
		[],
	);

	const handleCertificateSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			setLoading(true);
			try {
				if (certificateModal.editing) {
					await dispatch(
						updateCertificate({
							token,
							certificateId: certificateModal.editing._id,
							data: certificateForm,
						}),
					).unwrap();
					showToast('Certificat modifié avec succès', 'success');
				} else {
					await dispatch(addCertificate({ token, data: certificateForm })).unwrap();
					showToast('Certificat ajouté avec succès', 'success');
				}
				await dispatch(getCertificates({ token })).unwrap();
				closeCertificateModal();
			} catch (err) {
				showToast(err?.message || "Quelque chose s'est mal passé !", 'error');
			} finally {
				setLoading(false);
			}
		},
		[dispatch, token, certificateForm, certificateModal, closeCertificateModal],
	);

	/* ── Delete ── */
	const handleDeleteActuality = useCallback((id) => {
		setDeleteModal({ open: true, type: 'actuality', id });
	}, []);

	const handleDeleteCertificate = useCallback((id) => {
		setDeleteModal({ open: true, type: 'certificate', id });
	}, []);

	const closeDeleteModal = useCallback(() => {
		setDeleteModal({ open: false, type: null, id: null });
	}, []);

	const confirmDelete = useCallback(async () => {
		const { type, id } = deleteModal;
		if (!id) return;

		closeDeleteModal();
		setLoading(true);
		try {
			if (type === 'actuality') {
				await dispatch(deleteActuality({ token, actualityId: id })).unwrap();
				showToast('Actualité supprimée avec succès', 'info');
			} else if (type === 'certificate') {
				await dispatch(deleteCertificate({ token, certificateId: id })).unwrap();
				showToast('Certificat supprimé avec succès', 'info');
			}
		} catch (err) {
			showToast(err?.message || "Quelque chose s'est mal passé !", 'error');
		} finally {
			setLoading(false);
		}
	}, [dispatch, token, deleteModal, closeDeleteModal]);

	return (
		<div className='cp-root'>
			{/* ── Header ── */}
			<header className='cp-header'>
				<div className='cp-header-inner'>
					{company?.image?.secure_url && (
						<div className='cp-logo-wrap'>
							<img
								src={company.image.secure_url}
								alt={`Logo ${company.name}`}
								className='cp-logo'
							/>
						</div>
					)}
					<div>
						<h1 className='cp-title'>Société : {company?.name || 'Mon entreprise'}</h1>
						<p className='cp-subtitle'>Gestion du profil</p>
					</div>
				</div>
				<nav className='cp-tabs' aria-label='Sections du profil'>
					{[
						{ id: 'info', label: 'Informations' },
						{
							id: 'actualites',
							label: `Actualités${actualites?.length ? ` (${actualites.length})` : ''}`,
						},
						{
							id: 'certificats',
							label: `Certificats${certificats?.length ? ` (${certificats.length})` : ''}`,
						},
					].map((t) => (
						<button
							key={t.id}
							type='button'
							className={`cp-tab ${activeTab === t.id ? 'active' : ''}`}
							onClick={() => setActiveTab(t.id)}
						>
							{t.label}
						</button>
					))}
				</nav>
			</header>

			<main className='cp-main'>
				{/* ── INFO TAB ── */}
				{activeTab === 'info' && (
					<form className='cp-form' onSubmit={handleCompanySubmit} noValidate>
						<div className='cp-section-title'>Informations générales</div>

						<ImageUploader
							id='company-logo'
							label="Logo de l'entreprise"
							value={companyForm.image}
							token={token}
							variant="default"
							onChange={(img) => setCompanyForm((prev) => ({ ...prev, image: img }))}
						/>

						<div className='cp-grid'>
							<div className='field'>
								<label htmlFor='field-name'>Nom *</label>
								<input
									id='field-name'
									name='name'
									value={companyForm.name}
									onChange={handleCompanyChange}
									required
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-email'>Email</label>
								<input
									id='field-email'
									type='email'
									name='email'
									value={companyForm.email}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-phone'>Téléphone</label>
								<input
									id='field-phone'
									type='tel'
									name='phone'
									value={companyForm.phone}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-fax'>Fax</label>
								<input
									id='field-fax'
									name='fax'
									value={companyForm.fax}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-siteweb'>Site Web</label>
								<input
									id='field-siteweb'
									name='siteweb'
									value={companyForm.siteweb}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field full'>
								<label htmlFor='field-address'>Adresse</label>
								<input
									id='field-address'
									name='address'
									value={companyForm.address}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-tva'>Code TVA</label>
								<input
									id='field-tva'
									name='tva_code'
									value={companyForm.tva_code}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-rc'>R.C</label>
								<input
									id='field-rc'
									name='r_c'
									value={companyForm.r_c}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-customs'>Code douane</label>
								<input
									id='field-customs'
									name='customs_code'
									value={companyForm.customs_code}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-sponsorship'>Code parrainage</label>
								<input
									id='field-sponsorship'
									name='sponsorship_code'
									value={companyForm.sponsorship_code}
									disabled
									readOnly
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-date'>Date de création</label>
								<input
									id='field-date'
									type='date'
									name='date_creation_societe'
									value={companyForm.date_creation_societe}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-nombre-employes'>Nombre d'employés</label>
								<input
									id='field-nombre-employes'
									type='number'
									name='nombre_employes'
									min='0'
									step='1'
									value={companyForm.nombre_employes}
									onChange={handleCompanyChange}
								/>
							</div>
							<div className='field'>
								<label htmlFor='field-company-type'>Type de société</label>
								<select
									id='field-company-type'
									name='company_type'
									value={companyForm.company_type}
									onChange={handleCompanyChange}
								>
									{companyTypes.map((type) => (
										<option key={type.value} value={type.value}>
											{type.label}
										</option>
									))}
								</select>
							</div>
						</div>

						{/* Catégories d'économie */}
						<div className='cp-section-title' style={{ marginTop: '2rem' }}>
							Catégories d'économie
						</div>
						<div className='economy-categories-group'>
							{economyCategories.map((category) => (
								<label key={category.value} className='checkbox-label'>
									<input
										type='checkbox'
										checked={companyForm.categories_economie.includes(category.value)}
										onChange={() => handleCategoryChange(category.value)}
									/>
									<span className='checkbox-custom'></span>
									{category.label}
								</label>
							))}
						</div>

						<div className='cp-form-footer'>
							<button type='submit' className='btn btn-primary' disabled={loading}>
								{loading ? (
									<>
										<span className='btn-spinner' aria-hidden='true' /> Enregistrement...
									</>
								) : (
									'Enregistrer les modifications'
								)}
							</button>
						</div>
					</form>
				)}

				{/* ── ACTUALITES TAB ── */}
				{activeTab === 'actualites' && (
					<section className='cp-list-section' aria-label='Actualités'>
						<div className='list-header'>
							<span className='list-count'>{actualites?.length || 0} actualité(s)</span>
							<button
								type='button'
								className='btn btn-primary'
								onClick={() => openActualityModal()}
							>
								+ Ajouter
							</button>
						</div>
						{actualites?.length > 0 ? (
							<div className='cards-grid'>
								{actualites.map((a) => (
									<ActualityCard
										key={a._id}
										actuality={a}
										onEdit={openActualityModal}
										onDelete={handleDeleteActuality}
									/>
								))}
							</div>
						) : (
							<div className='empty-state'>
								<p>Aucune actualité pour le moment</p>
								<button
									type='button'
									className='btn btn-primary'
									onClick={() => openActualityModal()}
								>
									Créer la première
								</button>
							</div>
						)}
					</section>
				)}

				{/* ── CERTIFICATS TAB ── */}
				{activeTab === 'certificats' && (
					<section className='cp-list-section' aria-label='Certificats'>
						<div className='list-header'>
							<span className='list-count'>{certificats?.length || 0} certificat(s)</span>
							<button
								type='button'
								className='btn btn-primary'
								onClick={() => openCertificateModal()}
							>
								+ Ajouter
							</button>
						</div>
						{certificats?.length > 0 ? (
							<div className='cards-grid certs'>
								{certificats.map((c) => (
									<CertificateCard
										key={c._id}
										certificate={c}
										onEdit={openCertificateModal}
										onDelete={handleDeleteCertificate}
									/>
								))}
							</div>
						) : (
							<div className='empty-state'>
								<p>Aucun certificat pour le moment</p>
								<button
									type='button'
									className='btn btn-primary'
									onClick={() => openCertificateModal()}
								>
									Ajouter un certificat
								</button>
							</div>
						)}
					</section>
				)}
			</main>

			{/* ── ACTUALITY MODAL ── */}
			{actualityModal.open && (
				<Modal
					title={actualityModal.editing ? "Modifier l'actualité" : 'Nouvelle actualité'}
					onClose={closeActualityModal}
					onSubmit={handleActualitySubmit}
					loading={loading}
					submitLabel={actualityModal.editing ? 'Modifier' : 'Ajouter'}
				>
					<div className='field'>
						<label htmlFor='act-title'>Titre *</label>
						<input
							id='act-title'
							value={actualityForm.title}
							onChange={(e) => setActualityForm((p) => ({ ...p, title: e.target.value }))}
							required
						/>
					</div>
					<div className='field'>
						<label htmlFor='act-desc'>Description</label>
						<textarea
							id='act-desc'
							rows={4}
							value={actualityForm.description}
							onChange={(e) => setActualityForm((p) => ({ ...p, description: e.target.value }))}
						/>
					</div>
					<ImageUploader
						id='act-image'
						label="Image de l'actualité"
						value={actualityForm.image}
						token={token}
						variant="modal"
						onChange={(img) => setActualityForm((p) => ({ ...p, image: img }))}
					/>
				</Modal>
			)}

			{/* ── CERTIFICATE MODAL ── */}
			{certificateModal.open && (
				<Modal
					title={certificateModal.editing ? 'Modifier le certificat' : 'Nouveau certificat'}
					onClose={closeCertificateModal}
					onSubmit={handleCertificateSubmit}
					loading={loading}
					submitLabel={certificateModal.editing ? 'Modifier' : 'Ajouter'}
				>
					<div className='field'>
						<label htmlFor='cert-name'>Nom du certificat *</label>
						<input
							id='cert-name'
							value={certificateForm.name}
							onChange={(e) => setCertificateForm((p) => ({ ...p, name: e.target.value }))}
							required
						/>
					</div>
					<ImageUploader
						id='cert-image'
						label='Image du certificat'
						value={certificateForm.image}
						token={token}
						required
						variant="modal"
						onChange={(img) => setCertificateForm((p) => ({ ...p, image: img }))}
					/>
				</Modal>
			)}

			{/* ── DELETE CONFIRM MODAL ── */}
			{deleteModal.open && (
				<DeleteModal onClose={closeDeleteModal} onConfirm={confirmDelete} loading={loading} />
			)}
		</div>
	);
};

export default CompanyProfile;